
import { Services } from "@/services/services";
import { getEnv } from "@/util/env";
import type { AnyVariables, GraphQLRequest, OperationContext, OperationResult, Client as UrqlClient } from "@urql/core";

export const userLanguageHeaderName = "X-User-Language";
export const userCountryHeaderName = "X-User-Country";
export const userCategoryTypeHeaderName = "X-User-Category-Type";

type ValueOrGetter<T> = T | (() => Promise<T>);

type AppolloQueryParams<Data = any, Variables extends AnyVariables = AnyVariables> = Omit<GraphQLRequest<Data, Variables>, 'key' | 'variables' | 'query'> & { variables?: Variables, query: ValueOrGetter<GraphQLRequest<Data, Variables>['query']> };

type ApolloMutationParams<Data = any, Variables extends AnyVariables = AnyVariables> = Omit<GraphQLRequest<Data, Variables>, 'query' | 'key'> & { mutation: ValueOrGetter<GraphQLRequest<Data, Variables>['query']> };

export class Client {
  urql!: UrqlClient;

  constructor(protected api: Api) {

  }

  key = 0;

  async query<Data = any, Variables extends AnyVariables = AnyVariables>(
    query: AppolloQueryParams<Data, Variables>,
    opts?: Partial<OperationContext & { controller: AbortController }> | undefined,
  ) {
    await this.prepare();

    const realquery = typeof query.query === 'function'
      ? await query.query()
      : query.query;
    const name = this.getQueryName(realquery) ?? '';
    const url = this.getUrl(name);

    const source = this.urql.executeQuery({
      ...query,
      query: realquery,
      variables: query.variables ?? {} as Variables,
      key: ++this.key
    }, { ...opts, url, });

    const response = await new Promise((resolve, reject) => {
      const subscription = source.subscribe(result => {
        subscription.unsubscribe();

        resolve(result)
      });

      opts?.controller?.signal.addEventListener('abort', () => {
        subscription.unsubscribe();
        reject(new Error('Request aborted'));
      });
    }) as OperationResult<Data, Variables>;

    const data = response.data!;
    const errors = response.error?.graphQLErrors!;
    const error = errors?.[0];

    if (errors) {
      this.api.services.alerts.addMessages(errors.map(error => error.message), 'danger');
    }

    return { ...response, data, error, errors };
  }

  async mutate<Data = any, Variables extends AnyVariables = AnyVariables>(query: ApolloMutationParams<Data, Variables>, opts?: Partial<OperationContext> | undefined) {
    await this.prepare();

    const realquery = typeof query.mutation === 'function'
      ? await query.mutation()
      : query.mutation;
    const name = this.getQueryName(realquery) ?? '';
    const url = this.getUrl(name);
    const response = await this.urql.executeMutation({
      ...query,
      query: realquery,
      key: ++this.key
    }, { ...opts, url }) as OperationResult<Data, Variables>;

    const data = response.data!;
    const errors = response.error?.graphQLErrors!;
    const error = errors?.[0];

    return { ...response, data, error, errors };
  }

  getUrl(name: string) {
    return `${this.api.base}/${this.getEndpoint(name)}`;
  }

  getEndpoint(name: string) {
    switch (name) {
      case "Currencies":
      case "Countries":
        return "generic";
      case "Customer":
        return "customer";
      default:
        return "b2c";
    }
  }

  getQueryName(query: GraphQLRequest['query']) {
    return query.definitions.find((def) => def.kind === "OperationDefinition")?.name?.value;
  }

  async init() {
    const { Client: UrqlClient, fetchExchange } = await import('@urql/core');

    this.urql = new UrqlClient({
      url: `${this.api.base}/b2c`,
      exchanges: [fetchExchange],
      fetchOptions: () => {
        const { lang, categoryType } = this.api;
        const result = { headers: {}, credentials: 'include' } satisfies RequestInit as { headers: Record<string, string> };

        if (lang) {
          result.headers[userLanguageHeaderName] = lang;
        }

        if (categoryType) {
          result.headers[userCategoryTypeHeaderName] = categoryType;
        }

        result.headers["cookie"] = document.cookie;

        return result as RequestInit;
      },
      requestPolicy: "network-only",
    });
  }

  async prepare() {
    if (!this.urql) {
      await this.init();
    }
  }
}

export class Api {
  host = `${window.location.origin}/api`;
  base = `${this.host}`;

  client: Client;

  public services!: Services;

  constructor() {
    this.client = new Client(this);
  }

  get lang() {
    return this.services.parameters.parameters?.language;
  }

  get country() {
    return this.services.parameters.parameters?.country;
  }

  get categoryType() {
    return 'passenger';
  }
}